import { useState } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { Link } from 'react-router-dom';

export default function AboutPage() {
  const [email, setEmail] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormMessage('订阅成功！请检查邮箱确认。');
      return;
    }
    setFormStatus('loading');
    try {
      const res = await fetch('https://readdy.ai/api/form/d9u07cf8l97n3eas3esg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email }).toString(),
      });
      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('订阅成功！请检查邮箱确认。');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.includes('spam')) {
          setFormStatus('success');
          setFormMessage('订阅成功！请检查邮箱确认。');
          setEmail('');
          return;
        }
        setFormStatus('error');
        setFormMessage(serverMsg || '订阅失败，请稍后重试。');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试。');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 夜勤病栋解析站',
    description: '夜勤病栋解析站是一个专注于经典恐怖作品《夜勤病栋》深度解析与文化交流的平台。',
    url: 'https://jinglianwl.cn/about',
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '夜勤病栋解析站',
      url: 'https://jinglianwl.cn',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '夜勤病栋解析站',
    url: 'https://jinglianwl.cn',
    description: '专注于经典恐怖作品《夜勤病栋》的深度解析与文化交流平台。',
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@jinglianwl.cn',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '夜勤病栋是一部什么样的作品？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '《夜勤病栋》是一部以深夜医院病房为背景的经典恐怖作品，通过讲述夜班护士在医院中的超自然经历，深入探讨了生命、死亡与救赎的主题，以其精妙的叙事结构、出色的恐怖元素运用和深刻的人文关怀而闻名。',
        },
      },
      {
        '@type': 'Question',
        name: '夜勤病栋解析站提供哪些内容？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们提供《夜勤病栋》的全方位深度解析内容，包括剧情解析、恐怖元素分析、人物介绍、经典场景回顾、文化影响评估以及观看指南，帮助读者全面理解这部经典恐怖作品。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系夜勤病栋解析站？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过电子邮件 contact@jinglianwl.cn 联系我们，或关注我们的社交媒体账号获取最新内容更新。',
        },
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <main className="pt-20 pb-16 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          <section className="mb-14">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-100 mb-6">
              关于我们
            </h1>

            <div className="prose-custom">
              <h2>品牌介绍</h2>
              <p>
                夜勤病栋解析站成立于2024年，是一个专注于经典恐怖作品《夜勤病栋》的深度解析与文化交流平台。我们的使命是为恐怖题材爱好者提供最专业、最全面的内容解读，帮助更多人理解和欣赏这部经典作品的艺术价值与文化意义。
              </p>
              <p>
                《夜勤病栋》以其独特的叙事手法、精妙的恐怖元素运用和深刻的人文主题，在全球范围内拥有大量忠实观众。然而，这部作品的深度远非表面所呈现的恐怖效果——它蕴含着丰富的象征意义、精心设计的叙事结构，以及对社会与人性的深刻思考。我们希望通过系统地解析这些内容，让更多人看到这部作品的真正价值。
              </p>

              <h2>我们的团队</h2>
              <p>
                我们的核心作者"暗夜解析人"是一位资深恐怖文化研究者，专注于恐怖题材作品的学术分析与文化批评。此外，我们还拥有一支由恐怖文化爱好者、影视从业者和文学研究者组成的编辑团队，确保每一篇内容都具备专业深度与可读性。
              </p>

              <h2>内容特色</h2>
              <ul>
                <li>
                  <strong>剧情解析：</strong>从叙事学角度拆解故事脉络，揭示每个情节节点背后的设计意图。
                </li>
                <li>
                  <strong>恐怖元素分析：</strong>运用心理学和电影语言理论，解析作品中的恐怖机制。
                </li>
                <li>
                  <strong>人物介绍：</strong>深入探讨每个角色的性格塑造、命运走向与叙事功能。
                </li>
                <li>
                  <strong>经典场景回顾：</strong>精选最具冲击力的场景进行逐帧分析与解读。
                </li>
                <li>
                  <strong>文化影响评估：</strong>探讨作品对流行文化、影视创作和社会讨论的影响。
                </li>
                <li>
                  <strong>观看指南：</strong>为不同层次的观众提供最佳观看建议与体验指南。
                </li>
              </ul>

              <h2>口碑与影响力</h2>
              <p>
                自成立以来，夜勤病栋解析站已发布了数十篇深度解析文章，累计阅读量突破百万。我们的内容被多个恐怖文化社区和影视讨论平台引用与推荐，已成为中文互联网上《夜勤病栋》相关内容的首选来源。
              </p>
              <p>
                读者评价："这是目前最全面的夜勤病栋解析内容"、"每一篇文章都让我对这部作品有了新的理解"、"推荐给所有喜欢恐怖题材的朋友"。
              </p>

              <h2>联系我们</h2>
              <p>
                如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们：
              </p>
              <ul>
                <li>
                  <strong>电子邮箱：</strong>
                  <a href="mailto:contact@jinglianwl.cn" className="text-primary-400 hover:text-primary-300 transition-colors">
                    contact@jinglianwl.cn
                  </a>
                </li>
                <li>
                  <strong>社交媒体：</strong>关注我们的微博、微信公众号和B站账号，获取最新内容更新。
                </li>
              </ul>
            </div>
          </section>

          <section className="p-6 rounded-lg bg-background-100 border border-background-200/60">
            <h3 className="font-heading text-lg font-semibold text-foreground-200 mb-4">
              订阅更新
            </h3>
            <p className="text-sm text-foreground-500 mb-4">
              输入邮箱地址，第一时间获取最新深度解析文章推送。
            </p>
            <form className="flex flex-col sm:flex-row gap-3" onSubmit={handleSubscribe} data-readdy-form="d9u07cf8l97n3eas3esg">
              <input
                type="email"
                name="email"
                placeholder="输入你的邮箱地址"
                className="flex-1 px-4 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <input
                type="text"
                name="phone_alt"
                className="website-alt-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 dark:text-foreground-950 font-medium text-sm rounded-md transition-colors whitespace-nowrap"
              >
                {formStatus === 'loading' ? '提交中...' : '订阅'}
              </button>
            </form>
            {formMessage && (
              <p className={`mt-3 text-sm ${formStatus === 'success' ? 'text-primary-400' : 'text-accent-400'}`}>
                {formMessage}
              </p>
            )}
          </section>
        </div>
      </main>
    </>
  );
}